import React, { useCallback, useEffect, useState } from 'react'
import { FolderView } from './FolderView'
import { useIntl } from 'react-intl'
import { ModalDocument } from './Modals/modalDocument'
import DocManagementService from '../../../../../API/Documents/DocumentManagement'
import Loading from '../../../../../Components/Loading/Loading'
import { getEmployeesByCompany } from '../../../../../API/Employee/Employee'
import CreateFolderModal from './Modals/createFolder'

function getFolderPathById(folderId, folders) {
	const path = []
	let current = folders.find(f => f.id === folderId)
	while (current) {
		path.unshift(current)
		current = folders.find(f => f.id === current.parentId)
	}
	return path
}

function useFolderNavigation(folders, employeesList) {
	const [folderId, setFolderId] = useState(null)
	const [selectedEmployeeId, setSelectedEmployeeId] = useState(null)
	const individualRoot = folders.find(f => f.private === true && f.parentId === null)
	const currentFolder = folderId ? folders.find(f => f.id === folderId) : null

	const handleOpen = id => {
		if (currentFolder?.id === individualRoot?.id && employeesList.some(e => e.id === id)) {
			setSelectedEmployeeId(id)
		} else {
			setFolderId(id)
		}
	}

	const handleBreadcrumbClick = item => {
		if (item.name === 'Início') {
			setFolderId(null)
			setSelectedEmployeeId(null)
		} else if (item.id === individualRoot?.id) {
			setFolderId(individualRoot.id)
			setSelectedEmployeeId(null)
		} else if (typeof item.id === 'string' && item.id.startsWith('employee-')) {
			setSelectedEmployeeId(Number(item.id.replace('employee-', '')))
			setFolderId(individualRoot.id)
		} else {
			setFolderId(item.id)
		}
	}

	return {
		folderId,
		selectedEmployeeId,
		currentFolder,
		individualRoot,
		handleOpen,
		handleBreadcrumbClick,
	}
}

function useFolderContent(currentFolder, individualRoot, selectedEmployeeId, folders, employeesList) {
	if (currentFolder?.id === individualRoot?.id) {
		if (!selectedEmployeeId) {
			return {
				folders: employeesList.map(emp => ({
					id: emp.id,
					name: emp.name,
					isEmployeeVirtual: true,
				})),
				documents: [],
			}
		}
		return {
			folders: folders.filter(
				f => f.private && f.parentId === individualRoot.id && f.employees?.length === 1 && f.employees[0].id === selectedEmployeeId
			),
			documents: [],
		}
	}
	return {
		folders: folders.filter(f => f.parentId === (currentFolder?.id ?? null)),
		documents: currentFolder?.folderDocuments || currentFolder?.documents || [],
	}
}

function useBreadcrumb(folderId, folders, selectedEmployeeId, employeesList, individualRoot) {
	const pathChain = folderId ? getFolderPathById(folderId, folders) : []
	const breadcrumb = [{ id: null, name: 'Início' }]
	if (pathChain.length) {
		breadcrumb.push(...pathChain.map(folder => ({ id: folder.id, name: folder.name })))
	}
	if (selectedEmployeeId && pathChain[0]?.id === individualRoot?.id) {
		const employee = employeesList.find(e => e.id === selectedEmployeeId)
		if (employee) {
			breadcrumb.splice(2, 0, {
				id: `employee-${selectedEmployeeId}`,
				name: employee.name,
			})
		}
	}
	return breadcrumb
}

export const Breadcrumb = ({ items, onClick }) => (
	<nav className='tw-flex tw-items-center tw-gap-2 tw-text-base tw-text-gray-500 tw-mt-4'>
		{items.map((item, i) => {
			const isLast = i === items.length - 1
			return (
				<span key={item.id || item.name} className='tw-flex tw-items-center tw-gap-2'>
					{i > 0 && <span>/</span>}
					<span
						className={!isLast ? 'tw-text-purple-500 tw-cursor-pointer hover:tw-text-indigo-600' : ''}
						onClick={!isLast ? () => onClick(item) : undefined}
					>
						{item.name}
					</span>
				</span>
			)
		})}
	</nav>
)

export const DocumentManager = () => {
	const { locale } = useIntl()
	const language = locale === 'PT-BR' ? 'pt' : 'es'
	const companyId = JSON.parse(localStorage.getItem('user')).id

	const [folders, setFolders] = useState([])
	const [loading, setLoading] = useState(false)
	const [employeesList, setEmployeesList] = useState([])
	const [isModalDocumentOpen, setIsModalDocumentOpen] = useState(false)
	const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false)

	const { folderId, selectedEmployeeId, currentFolder, individualRoot, handleOpen, handleBreadcrumbClick } = useFolderNavigation(
		folders,
		employeesList
	)

	const { folders: foldersToShow, documents: documentsToShow } = useFolderContent(
		currentFolder,
		individualRoot,
		selectedEmployeeId,
		folders,
		employeesList
	)

	const breadcrumb = useBreadcrumb(folderId, folders, selectedEmployeeId, employeesList, individualRoot)

	const loadFolders = useCallback(async () => {
		setLoading(true)
		try {
			const [respEmployees, respFolders] = await Promise.all([getEmployeesByCompany(companyId), DocManagementService.findFolders(companyId)])
			setFolders(respFolders)
			setEmployeesList(respEmployees.sort((a, b) => a.name.localeCompare(b.name)))
		} catch (error) {
			console.error('Error loading folders:', error)
		} finally {
			setLoading(false)
		}
	}, [companyId])

	useEffect(() => {
		loadFolders()
	}, [loadFolders])

	const formatDate = useCallback(
		timestamp => {
			const date = new Date(timestamp)
			const str = date.toLocaleDateString(language === 'pt' ? 'pt-BR' : 'es-CL', {
				day: '2-digit',
				month: 'long',
				year: 'numeric',
			})
			return str.charAt(0).toUpperCase() + str.slice(1)
		},
		[language]
	)

	//actions to folders
	const handleDelete = useCallback(
		async id => {
			setLoading(true)
			try {
				await DocManagementService.deleteFolder(id)
				await loadFolders()
			} catch (error) {
				console.error('Erro ao deletar pasta:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadFolders]
	)

	const handleMove = useCallback(
		async (folderId, newParentId) => {
			setLoading(true)
			try {
				await DocManagementService.moveFolder(folderId, { parentId: newParentId })
				await loadFolders()
			} catch (error) {
				console.error('Erro ao mover pasta:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadFolders]
	)

	const handleRename = useCallback(
		async (folderId, newName) => {
			setLoading(true)
			try {
				await DocManagementService.updateFolder(folderId, { name: newName })
				await loadFolders()
			} catch (error) {
				console.error('Erro ao renomear pasta:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadFolders]
	)

	const handleAssignEmployees = useCallback(
		async (employeesId, folderId) => {
			setLoading(true)
			try {
				const data = { employeeIds: employeesId }

				await DocManagementService.updateFolder(folderId, data)
				await loadFolders()
			} catch (error) {
				console.error('Erro ao atribuir colaboradores:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadFolders]
	)

	//actions to documents

	const handleDocumentSubmit = useCallback(
		async (data, files) => {
			setLoading(true)
			try {
				const uploadRoutes = await DocManagementService.uploadDocuments(data)
				await Promise.all(
					uploadRoutes.map(({ url }, i) =>
						fetch(url, {
							method: 'PUT',
							body: files[i],
							headers: { 'Content-Type': files[i].type },
						})
					)
				)
				await loadFolders()
				setIsModalDocumentOpen(false)
			} catch (error) {
				console.error('Erro ao subir documentos:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadFolders]
	)

	const handleDeleteDocument = useCallback(
		async documentId => {
			setLoading(true)
			try {
				await DocManagementService.deleteDocument(documentId)
				await loadFolders()
			} catch (error) {
				console.error('Erro ao deletar documento:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadFolders]
	)

	const handleMoveDocument = useCallback(
		async (documentId, newFolderId) => {
			setLoading(true)
			try {
				await DocManagementService.moveDocument(documentId, { folderId: newFolderId })
				await loadFolders()
			} catch (error) {
				console.error('Erro ao mover documento:', error)
			} finally {
				setLoading(false)
			}
		},
		[loadFolders]
	)

	return (
		<>
			<div className='tw-flex tw-items-start tw-justify-between tw-mb-4 tw-pr-10'>
				<div>
					<h3 className='fw-bold tw-mb-4'>Gestión de Documentos</h3>
					<div>
						Organiza y comparte documentos essenciais de la empresa, ya sejam corporativos, por departamento ou pessoais, asegurando que cada
						colaborador acceda solo a lo que le corresponde.
					</div>
				</div>
				<div className='tw-flex tw-gap-2 tw-flex-nowrap'>
					<div
						className='border tw-flex-nowrap tw-px-4 tw-py-2 tw-text-gray-700 hover:tw-bg-gray-50 tw-cursor-pointer tw-rounded-lg tw-whitespace-nowrap'
						onClick={() => setIsCreateFolderModalOpen(true)}
					>
						Crear Nueva Carpeta
					</div>
					<div
						className='tw-bg-purple-500 tw-flex-nowrap tw-text-white tw-px-4 tw-py-2 hover:tw-bg-purple-700 tw-cursor-pointer tw-rounded-lg tw-whitespace-nowrap'
						onClick={() => setIsModalDocumentOpen(true)}
					>
						Subir Nuevo Documento
					</div>
				</div>
			</div>

			<div className='tw-text-sm tw-text-gray-500 tw-mb-2'>
				Los documentos de la carpeta Común y sus subcarpetas pueden ser accedidos por varios colaboradores al mismo tiempo. Ejemplo: Código de
				Conducta.
			</div>
			<div className='tw-text-sm tw-text-gray-500 tw-mb-6'>
				Los documentos de la carpeta Individual solo podrán ser accedidos por el colaborador correspondiente. Ejemplo: Recibo de salario.
			</div>

			{loading ? (
				<Loading />
			) : (
				<>
					<Breadcrumb items={breadcrumb} onClick={handleBreadcrumbClick} />

					<FolderView
						folders={foldersToShow}
						documents={documentsToShow}
						onOpen={handleOpen}
						formatDate={formatDate}
						onDelete={handleDelete}
						onMove={handleMove}
						employeesList={employeesList}
						modalFolders={folders}
						onRename={handleRename}
						allowClick={true}
						currentFolder={currentFolder}
						onDeleteDocument={handleDeleteDocument}
						onMoveDocument={handleMoveDocument}
						onAssign={handleAssignEmployees}
					/>

					<CreateFolderModal
						isOpen={isCreateFolderModalOpen}
						onClose={() => setIsCreateFolderModalOpen(false)}
						folders={folders}
						employeesList={employeesList}
						onCreated={loadFolders}
						companyId={companyId}
					/>

					<ModalDocument
						isOpen={isModalDocumentOpen}
						folders={folders}
						onClose={() => setIsModalDocumentOpen(false)}
						onSubmit={handleDocumentSubmit}
						employeesList={employeesList}
					/>
				</>
			)}
		</>
	)
}
